import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Material UI
import { SxProps } from "@mui/material";
import { GRID_TREE_DATA_GROUPING_FIELD, GridInitialState, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Context
import { useCitContextData } from "@DataPages/context/cit/CitContext";
// Components
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { CitDataTableTitle } from "../CitDataTableTitle";
// Hooks
import { useCITTableColumns } from "../hooks/useCitTableConfiguration";
// Types
import { ICitActivity } from "@/Common/types/ContractsInTransit/model";
import { DataSmartTableProps } from "@/Layouts/components/types";
// Constants
import { DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";
// Utils
import { generateSx, getRowClassName, getRowId } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { tableKeys } from "@/Constants/tableKeys";

// Constant configurations
const columnsToExpand = [
	"deskManager",
	"salesManager",
	"salesPerson",
	"creationDate",
	"description",
	"glDesc",
	"vinNumber",
	"customerName",
	"fIDate",
	"postingDate",
	"tenDaySent",
	"stage",
	"trade",
	"controlType",
];

const pinnedColumns = {
	left: [GRID_TREE_DATA_GROUPING_FIELD, "__isFunded", "transactionDate", "dealNumber", "balance", "control", "__empty"],
};

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
};

const customProps: DataSmartTableProps["customProps"] = {
	tableContainerClassName: "[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
	tableClassName: {
		"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
		"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
		"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row))_.MuiDataGrid-cell--pinnedLeft:not(.empty-row)]:shadow-table-line": true,
		"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
		"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row)):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-line-hov":
			true,
	},
	expandableRows: false,
	hide: false,
};

const countUniqueValues = (rows: ICitActivity[], key: keyof ICitActivity) => {
	return new Set(rows.map((row) => row[key])).size;
};

const sumTotalValues = (rows: ICitActivity[], key: keyof ICitActivity) => {
	return rows.reduce((accumulator, currentValue) => accumulator + (currentValue[key] as number), 0);
};

const columnVisibilityModel = {
	_id: false,
};

const initialState: GridInitialState = {
	aggregation: {
		model: {
			_id: "size",
		},
	},
};

interface ICITDataTableProps {
	isFetching: boolean;
}

const isParentCitActivity = (activity: ICitActivity) => Boolean(activity?.hierarchy?.length === 1);

export default function CITDataTable(props: Readonly<ICITDataTableProps>) {
	const { receivables, citSettings, setFilteredReceivables, isFundable } = useCitContextData();

	const apiRef = useGridApiRef();

	const prevCurrent = useRef<string[]>([]);
	const [dealNumberCountFooter, setDealNumberCountFooter] = useState(0);
	const [vinNumberCountFooter, setVinNumberCountFooter] = useState(0);
	const [balanceFooter, setBalanceFooter] = useState(0);
	const [amountFinancedFooter, setAmountFinancedFooter] = useState(0);
	const [ageFooter, setAgeFooter] = useState(0);
	const [transactionAmountFooter, setTransactionAmountFooter] = useState(0);

	const { columns, groupingColDef } = useCITTableColumns({
		dealNumberCountFooter,
		vinNumberCountFooter,
		balanceFooter,
		amountFinancedFooter,
		ageFooter,
		transactionAmountFooter,
	});

	const onStateChange = useCallback(() => {
		if (!apiRef.current) {
			return;
		}

		const state = apiRef.current.state.filter;

		let currentGroupIds: string[] = [];
		let currentChildrenIds: string[] = [];
		const rowModels = apiRef.current.state.rows.dataRowIdToModelLookup;
		let allReceivables = apiRef.current.state.rows.dataRowIds.map((id) => rowModels[id] as ICitActivity);
		const filtered = state.filteredDescendantCountLookup || null;

		if (filtered) {
			currentGroupIds = Object.keys(filtered).filter(
				(key) => isParentCitActivity(rowModels?.[key] as ICitActivity) && !key.startsWith("auto-generated")
			);
			currentChildrenIds = Object.keys(filtered).filter((key) => !isParentCitActivity(rowModels?.[key] as ICitActivity));
			allReceivables = Object.keys(filtered)
				.filter((key) => !key.startsWith("auto-generated"))
				.map((id) => rowModels[id] as ICitActivity);
		}

		if (JSON.stringify(prevCurrent?.current) !== JSON.stringify(currentGroupIds)) {
			const groupReceivables = allReceivables?.filter((row) => currentGroupIds.includes(row._id));
			const childrenReceivables = allReceivables?.filter((row) => currentChildrenIds.includes(row._id));
			const scheduledReceivables = groupReceivables.filter(isFundable);
			const ageTotal = sumTotalValues(scheduledReceivables, "age");
			const rowsDataCitCount = scheduledReceivables.length ?? 1;

			// Calculate the footer values
			setDealNumberCountFooter(
				countUniqueValues(
					groupReceivables.filter((row) => row.dealNumber),
					"dealNumber"
				)
			);
			setVinNumberCountFooter(countUniqueValues(groupReceivables, "vinNumber"));
			setBalanceFooter(sumTotalValues(groupReceivables, "balance"));
			setAmountFinancedFooter(sumTotalValues(groupReceivables, "amountFinanced"));
			setAgeFooter(Math.round(ageTotal / rowsDataCitCount));
			setTransactionAmountFooter(
				sumTotalValues(groupReceivables, "transactionAmount") + sumTotalValues(childrenReceivables, "transactionAmount")
			);

			prevCurrent.current = currentGroupIds;
			setFilteredReceivables(prevCurrent.current);
		}
	}, []);

	const getTreeDataPath = useCallback((row: GridValidRowModel) => row.hierarchy as string[], []);

	useEffect(() => {
		if (!citSettings || !apiRef.current?.subscribeEvent) {
			return () => {};
		}

		const clearListener1 = apiRef.current?.subscribeEvent("filterModelChange", onStateChange);
		const clearListener2 = apiRef.current?.subscribeEvent("rowCountChange", onStateChange);

		onStateChange();

		return () => {
			clearListener1();
			clearListener2();
		};
	}, [receivables]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				titleExpandible: {
					className: "mt-[14px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
					hide: true,
				},
				customComponent: <CitDataTableTitle isFetching={props.isFetching} source={"Contracts In Transit"} />,
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
			},
			noRowsOverlay: {
				title: "No Contracts In Transit Data",
			},
		}),
		[props.isFetching, columnsToExpand]
	);

	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		setSx(generateSx(apiRef));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [props, apiRef]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.FNI_MANAGEMENT_CIT,
			}}
			apiRef={apiRef}
			autoHeight={false}
			columns={columns}
			rows={receivables}
			disableChildrenFiltering
			disableColumnMenu
			disableRowSelectionOnClick
			getRowClassName={getRowClassName}
			getRowId={getRowId}
			columnVisibilityModel={columnVisibilityModel}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			hideFooter
			initialState={initialState}
			pinnedColumns={pinnedColumns}
			rowHeight={25}
			sx={sx}
			treeData
			customProps={customProps}
			slotProps={slotProps}
			slots={slots}
			enableCustomScrollbars
			maxRows={20}
		/>
	);
}
